import React from 'react';
import { Calendar, MapPin, Clock, ArrowRight, Ticket, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

const events = [
  {
    title: 'Bortex Wedding Open Day',
    day: '18',
    month: 'OCT',
    year: '2026',
    time: 'Throughout the day',
    location: 'Bortex, A11 Industrial Estate, Marsa',
    description: 'Join us on 18th October for the Bortex Wedding Open Day. Planning your big day? Enjoy a relaxed and personal experience while our team guides you through our selection of groomswear and wedding attire, helping you find the perfect look for your celebration. Enjoy a glass of bubbly while you browse, ask questions, and receive expert advice from our experienced team. Free on-site parking is available throughout the day. Call +356 2132 1312 to secure your spot and take the first step towards your perfect wedding look with Bortex.',
    tag: 'OPEN DAY',
    status: 'BOOK YOUR SPOT',
    cta: 'JOIN THEM!',
    url: 'https://bortex.com.mt/',
    phone: '+356 2132 1312',
    image: '/images/events/bortex-wedding-open-day.jpg',
    imagePosition: 'center 25%',
  },
  {
    title: 'The Grand MFCC Weddings & Events Expo 2027',
    day: '04',
    month: 'FEB',
    year: '2027',
    time: 'Thu to Sun, from 18:00 weekdays',
    location: 'MFCC, Ta\' Qali',
    description: 'Malta\'s biggest annual wedding fair returns for its 28th edition: 100+ exhibitors covering venues, caterers, bridal wear, photography, entertainment and honeymoons under one roof. The 2026 edition ran 5 to 8 February; 2027 dates to be confirmed by MFCC.',
    tag: 'BRIDAL FAIR',
    status: 'ANNUAL, DATES TBC',
    cta: 'VIEW DETAILS',
    url: 'https://weddingsandeventsexpo.mt/',
    image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800&h=450&fit=crop',
  },
  {
    title: 'Corinthia Caterers Weddings Expo 2027',
    day: '17',
    month: 'APR',
    year: '2027',
    time: 'Weekend, 11:00 to 20:00',
    location: 'Waterfall Gardens, The Limestone Heritage, Siggiewi',
    description: 'Corinthia Caterers\' annual two-day weddings showcase: menu tastings, styled table settings, live entertainment and a hand-picked line-up of Malta\'s top wedding suppliers in the Waterfall Gardens. The 2026 edition ran 18 to 19 April; 2027 dates to be confirmed.',
    tag: 'EXPO',
    status: 'ANNUAL, DATES TBC',
    cta: 'VIEW DETAILS',
    url: 'https://corinthiacaterers.com/',
    image: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=800&h=450&fit=crop',
  },
];

const MONTHS = { JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5, JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11 };

const EventsSection = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingEvents = events.filter(
    (event) => new Date(Number(event.year), MONTHS[event.month], Number(event.day)) >= today
  );

  if (upcomingEvents.length === 0) return null;

  return (
    <section className="py-24" style={{ backgroundColor: '#FAF9F7' }} data-testid="events-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <p
            className="text-base md:text-lg mb-4 italic"
            style={{ fontFamily: '"Playfair Display", serif', color: '#DE92A9' }}
          >
            Mark your calendar
          </p>
          <h2
            className="text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6"
            style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}
          >
            Upcoming Events
          </h2>
          <p className="text-base max-w-2xl mx-auto leading-relaxed" style={{ color: '#757B7F' }}>
            From supplier open days to Malta's big bridal fairs, here is what's happening in the island's wedding scene. Dates are updated as soon as organisers confirm them.
          </p>
        </div>

        {/* Events List, invitation/ticket style */}
        <div className="space-y-6">
          {upcomingEvents.map((event) => {
            const isInternal = event.internal === true || event.url.startsWith('/');
            const isTel = event.url.startsWith('tel:');
            const cardClass =
              'group grid grid-cols-1 md:grid-cols-[140px_1fr] lg:grid-cols-[160px_320px_1fr_auto] bg-white rounded-xl overflow-hidden border border-[#E5E0DA] hover:shadow-xl hover:border-[#DE92A9]/30 transition-all duration-300';
            const cardTestId = `event-card-${event.title.toLowerCase().replace(/\s+/g, '-')}`;

            const cardInner = (
              <>
              {/* Date Stamp, like a calendar tear-off */}
              <div
                className="relative flex flex-col items-center justify-center py-8 md:py-0 px-6 text-white"
                style={{ background: 'linear-gradient(135deg, #DE92A9 0%, #E8AABC 100%)' }}
              >
                <span
                  className="text-xs tracking-[0.25em] font-medium opacity-90 mb-1"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  {event.month}
                </span>
                <span
                  className="text-5xl lg:text-6xl leading-none font-light"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  {event.day}
                </span>
                <span className="text-[10px] tracking-[0.2em] opacity-75 mt-2">{event.year}</span>

                {/* Decorative "ticket notches" */}
                <span className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#FAF9F7]"></span>
              </div>

              {/* Image */}
              <div className="relative overflow-hidden h-64 md:h-auto md:min-h-[180px] order-first md:order-none">
                <img
                  src={event.image}
                  alt={event.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  style={{ objectPosition: event.imagePosition || 'center' }}
                />
                <span
                  className="absolute top-4 left-4 text-[10px] tracking-[0.18em] font-semibold px-3 py-1.5 rounded-sm shadow-md"
                  style={{ backgroundColor: '#DE92A9', color: '#fff' }}
                >
                  {event.tag}
                </span>
                {event.status && (
                  <span className="absolute top-4 right-4 text-[10px] tracking-[0.18em] font-semibold px-3 py-1.5 rounded-sm bg-white/95 text-[#2C2C2C]">
                    {event.status}
                  </span>
                )}
              </div>

              {/* Content */}
              <div className="p-6 md:p-8 flex flex-col justify-center">
                <h3
                  className="text-xl md:text-2xl mb-3 leading-snug group-hover:text-[#DE92A9] transition-colors"
                  style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}
                >
                  {event.title}
                </h3>
                <p
                  className="text-sm leading-relaxed mb-4"
                  style={{ color: '#757B7F' }}
                >
                  {event.description}
                </p>
                <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs" style={{ color: '#2C2C2C' }}>
                  <span className="flex items-center gap-1.5">
                    <Clock size={13} className="text-[#DE92A9]" />
                    {event.time}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <MapPin size={13} className="text-[#DE92A9]" />
                    {event.location}
                  </span>
                  {event.phone && (
                    <span className="flex items-center gap-1.5 font-semibold" data-testid={`${cardTestId}-phone`}>
                      <Phone size={13} className="text-[#DE92A9]" />
                      {event.phone}
                    </span>
                  )}
                </div>
              </div>

              {/* CTA */}
              <div className="px-6 md:px-8 pb-6 md:pb-0 md:pr-8 flex items-center justify-start md:justify-end">
                <span
                  className="inline-flex items-center gap-2 text-xs tracking-[0.2em] font-semibold px-6 py-3.5 rounded-full transition-all group-hover:gap-3"
                  style={{ backgroundColor: '#DE92A9', color: '#fff' }}
                >
                  <Ticket size={14} />
                  {event.cta}
                  <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
              </>
            );

            return isInternal ? (
              <Link key={event.title} to={event.url} className={cardClass} data-testid={cardTestId}>
                {cardInner}
              </Link>
            ) : (
              <a
                key={event.title}
                href={event.url}
                target={isTel ? undefined : '_blank'}
                rel={isTel ? undefined : 'noopener noreferrer'}
                className={cardClass}
                data-testid={cardTestId}
              >
                {cardInner}
              </a>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-14">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-sm tracking-[0.15em] font-semibold px-8 py-4 border-2 transition-all duration-300 hover:gap-3 group"
            style={{ borderColor: '#DE92A9', color: '#DE92A9' }}
            data-testid="events-cta-btn"
          >
            <Calendar size={14} />
            HOST YOUR EVENT WITH US
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default EventsSection;
